/**
 * Canonical UTM attribution shape used across Worker, Browser and DB.
 * `utm_source` is the only required field for a valid attribution payload.
 */
export interface UtmAttribution {
  utm_source: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  /** Foreign-key style id pointing to an admin-managed UTM link. Pass-through only. */
  link_id?: number | null;
  /** Original URL query string without the leading `?`. */
  raw_query?: string;
  /** ISO timestamp captured at write time. */
  updated_at?: string;
}

/**
 * Cookie payload alias. Structurally identical to `UtmAttribution`,
 * kept as a separate name to express "this came from a cookie".
 */
export type UtmCookiePayload = UtmAttribution;

export const COOKIE_NAME = 'enter_utm';
export const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days
/** Default cookie domain in production / staging. Local dev should pass `domain: undefined` explicitly. */
export const COOKIE_DEFAULT_DOMAIN = '.converge.ai';

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export interface BuildCookieOptions {
  /** Defaults to `COOKIE_DEFAULT_DOMAIN`. Pass `undefined` to emit a host-scoped cookie (local dev). */
  domain?: string;
  /** Defaults to `COOKIE_MAX_AGE`. */
  maxAge?: number;
  /** Defaults to `'Lax'`. */
  sameSite?: 'Lax' | 'Strict' | 'None';
  /** Defaults to `true`. */
  secure?: boolean;
  /** Defaults to `'/'`. */
  path?: string;
}

/**
 * Type guard for `UtmAttribution`. Requires a non-empty string `utm_source`.
 */
export function isValidUtm(input: unknown): input is UtmAttribution {
  if (!input || typeof input !== 'object') return false;
  const obj = input as Record<string, unknown>;
  return typeof obj.utm_source === 'string' && obj.utm_source.length > 0;
}
